import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { uniqBy } from 'lodash';
import { useLazyQuery } from '@apollo/client';
import { CheckboxesOptions } from '../../../../../../../components/checkbox-select';
import { PHASES_COLORS } from '../../../../../../../const';
import { useInstallationContext } from '../../../../../../../context/installation';
import {
  AggregateVectorParameterTypeInternal,
  ChannelTypeInternal,
  MeasurementChannelKind,
  MeterTypeInternal,
  PersistedMeterAggregatedMeasurementsQueryVariables,
  Query,
} from '../../../../../../../data-access/gql-types/graphql';
import { PERSISTED_METER_AGGREGATED_MEASUREMENTS } from '../../../../../../../data-access/queries/meter';
import { MeasurementRange } from '../../../../../../../types';
import { getDateRangeByPage, getRangeLabel } from '../../../../../../../utils/charts/charts';
import { convertDateToString } from '../../../../../../../utils/helpers/date-convert';
import { useMeasurementMapOperations } from '../../../../../hooks/measurement-map/use-measurement-map-operations';
import * as storage from '../../../../../storage';
import { ChannelMeterType } from '../../../../../types';
import { useMeterDetailsContext } from '../../context';
import { AnalysisHookParams, ChartDataItem } from '../types';
import {
  assignDataFrom1IndexTo0,
  chartParameters,
  currentFormat,
  getCheckedParameter,
  getParameterColor,
  prepareParametersForRequest,
  priceAndCostParameters,
  separateBalancedActiveEnergyFotIncomingAndOutgoing,
} from '../utils/helpers';

export const useMeterAnalysisPersisted = ({ channel }: AnalysisHookParams) => {
  const { t } = useTranslation('channel-details');
  const { selectedInstallationId } = useInstallationContext();
  const { convertMeasurementToNumber, convertNumberToMeasurement, measurementMap } = useMeasurementMapOperations();
  const {
    aggregatedParameters,
    setAggregatedParameters,
    activeMeasurementRange,
    setActiveMeasurementRange,
    analysisPhases: selectedPhases,
    setAnalysisPhases: setSelectedPhases,
  } = useMeterDetailsContext();
  const [page, setPage] = useState<number>(0);
  const [getPersistedAggregatedMeasurements, { data, loading }] = useLazyQuery<
    Query,
    PersistedMeterAggregatedMeasurementsQueryVariables
  >(PERSISTED_METER_AGGREGATED_MEASUREMENTS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const channelData = channel?.data as ChannelMeterType | undefined;
  const tariffKind = channelData?.tariff?.kind;
  const supported = channelData?.supportedAggregateVectorParameters || [];

  const isStandalone =
    channelData?.type === ChannelTypeInternal.Meter && channelData?.meterType === MeterTypeInternal.Standalone;

  const defaultPhases = isStandalone
    ? [0, 1, 2, 3]
    : [0, ...(channel.data as ChannelMeterType).measuredChannels.map((x) => x.phase)];

  const getIndices = () => {
    return (channel.data as ChannelMeterType).measuredChannels.map((x) => x.phase);
  };

  const checkIfSupported = (param: AggregateVectorParameterTypeInternal) => {
    return supported.includes(convertMeasurementToNumber(MeasurementChannelKind.Meter)('aggregates', param));
  };

  const fromTo = useMemo(() => getDateRangeByPage(activeMeasurementRange, page), [activeMeasurementRange, page]);

  useEffect(() => {
    if (supported.length && !aggregatedParameters.length && measurementMap) {
      const convertedSupportedParams = supported.map((x) =>
        convertNumberToMeasurement(MeasurementChannelKind.Meter)('aggregates', x),
      ) as AggregateVectorParameterTypeInternal[];

      setAggregatedParameters(
        chartParameters.map(({ labelKey, type }) => ({
          label: t(`energyMeasurements.types.summary.${labelKey}`),
          value: type,
          checked: getCheckedParameter(type, convertedSupportedParams),
          disabled: !checkIfSupported(type),
        })),
      );
    }
  }, [supported, t, convertMeasurementToNumber, measurementMap, aggregatedParameters, tariffKind]);

  const getIndicesForRequest = () => {
    if (channelData?.type === ChannelTypeInternal.Meter && channelData?.meterType === MeterTypeInternal.BudgetPlug) {
      return selectedPhases;
    } else {
      return selectedPhases.length === 1 && selectedPhases[0] === 0 ? selectedPhases : [0, ...selectedPhases];
    }
  };

  useEffect(() => {
    if (!selectedPhases.length) return;

    const aggregateVectorRequestItems = prepareParametersForRequest(
      aggregatedParameters,
      activeMeasurementRange,
      checkIfSupported,
    );

    getPersistedAggregatedMeasurements({
      variables: {
        installationId: selectedInstallationId,
        input: {
          channelId: channel.id,
          deviceId: channel.deviceId,
          indices: getIndicesForRequest(),
          aggregateVectorRequestItems,
          from: fromTo.from,
          to: fromTo.to,
        },
      },
    });
  }, [aggregatedParameters, activeMeasurementRange, supported, selectedPhases, fromTo]);

  const onClickActiveMeasurementRange = (value: MeasurementRange) => {
    setActiveMeasurementRange(value);
    setPage(0);

    setAggregatedParameters((prev) => {
      return prev.map((x) => {
        if (value === MeasurementRange.DayInHours) {
          return {
            ...x,
            checked: x.checked,
            disabled: !checkIfSupported(x.value),
          };
        }

        return {
          ...x,
          checked: priceAndCostParameters.includes(x.value) ? false : x.checked,
          disabled: priceAndCostParameters.includes(x.value) ? true : x.disabled,
        };
      });
    });
  };

  const selectedParameters = aggregatedParameters.filter((x) => !x.disabled && x.checked);
  const selectedParametersWithNoPrice = selectedParameters.filter((x) => !priceAndCostParameters.includes(x.value));
  const singlePhaseChoice = selectedParametersWithNoPrice.length > 1;

  const handleSetAggregatedParameters = (options: CheckboxesOptions<AggregateVectorParameterTypeInternal>[]) => {
    setAggregatedParameters(options);

    const checkedParameters = options.filter((x) => x.checked).map((x) => x.value);
    const selectedParametersWithNoPrice = checkedParameters.filter((x) => !priceAndCostParameters.includes(x));

    if (!singlePhaseChoice && selectedParametersWithNoPrice.length > 1) {
      setSelectedPhases([0]);
    }

    storage.setItem('meterParameters', checkedParameters);
  };

  const error = data?.persistedMeterAggregatedMeasurements.error?.errorCode;
  const noData = !data?.persistedMeterAggregatedMeasurements.data?.items.length;

  const chartData: ChartDataItem[] = useMemo(() => {
    const items = data?.persistedMeterAggregatedMeasurements.data?.items || [];
    const channelIndices = getIndices();

    return uniqBy(
      items
        .filter((item) => {
          const itemDate = new Date(item.dateAtUtc);
          return itemDate >= new Date(fromTo.from) && itemDate <= new Date(fromTo.to);
        })
        .map((item) => {
          const date = new Date(item.dateAtUtc);
          let measurements = item.measurements as Record<AggregateVectorParameterTypeInternal, Record<number, number>>;

          if (activeMeasurementRange === MeasurementRange.DayInHours) {
            measurements = separateBalancedActiveEnergyFotIncomingAndOutgoing(measurements);
          }

          if (channelIndices.length === 1 && channelIndices[0] === 1) {
            measurements = assignDataFrom1IndexTo0(measurements);
          }

          return {
            date: date.getTime(),
            time: convertDateToString(date.getTime(), currentFormat(false, activeMeasurementRange)),
            tooltipTime: convertDateToString(date.getTime(), currentFormat(true, activeMeasurementRange)),
            values: Object.entries(measurements)
              .flatMap(([parameter, indicesObj]) => {
                const [measuerementType] = parameter.split('@');
                const type = measuerementType as AggregateVectorParameterTypeInternal;

                return Object.entries(indicesObj).map(([indice, value]) => {
                  const index = Number(indice);
                  const predictedCost = [AggregateVectorParameterTypeInternal.Cost].includes(type) && date > new Date();

                  return {
                    type,
                    index,
                    value: [AggregateVectorParameterTypeInternal.Price].includes(type) ? value : value / 1000,
                    color: index && !singlePhaseChoice ? PHASES_COLORS[index] : getParameterColor(type),
                    ...(predictedCost && { data: { predictedCost } }),
                  };
                });
              })
              .filter((x) =>
                selectedPhases.length === 1 && selectedPhases[0] === 0
                  ? x.index === 0
                  : [...selectedPhases].includes(x.index) || (x.index === 0 && priceAndCostParameters.includes(x.type)),
              ),
          };
        }),
      (x) => x.tooltipTime,
    );
  }, [data?.persistedMeterAggregatedMeasurements.data?.items, fromTo, singlePhaseChoice]);

  const currentRangeLabel = getRangeLabel(activeMeasurementRange, fromTo);

  const isPreviousData = useMemo(() => {
    const persistedData = data?.persistedMeterAggregatedMeasurements.data;
    if (persistedData) {
      if (!persistedData.oldestAtUtc) return true;

      const from = new Date(persistedData.from);
      const oldestAtUtc = new Date(persistedData.oldestAtUtc);

      return oldestAtUtc < from;
    }

    return false;
  }, [data?.persistedMeterAggregatedMeasurements]);

  return {
    chartData,
    currentRangeLabel,
    isLoading: loading && !data,
    selectedPhases,
    fromTo,
    setSelectedPhases,
    onClickActiveMeasurementRange,
    handleSetAggregatedParameters,
    aggregatedParameters,
    activeMeasurementRange,
    page,
    setPage,
    isStandalone,
    defaultPhases,
    error,
    isPreviousData,
    noData,
    selectedParameters,
    singlePhaseChoice,
  };
};
